import { useState, useContext, useRef, useEffect, useCallback } from 'react';

import '../CompaniesPage/CompaniesPage.css';
import '../DashboardOverview/DashboardOverview.css'

import { ProductFilled, DollarCircleFilled, BookFilled, SettingFilled, CalculatorFilled } from '@ant-design/icons';
import { FinancialBuilderContext } from '../FinancialBuilderPage.jsx';
import { RevenueVsCostChart, FinancialProjectionChart, RevenueGrowthChart, CostOverviewChart, BurnRateChart} from '../Charts/Charts.js'

import { CompanyDetails } from '../CompaniesPage/CompaniesPage.jsx';
import {genericPOSTDatabaseCall, SidebarButton, ShowAndHide, RenderOneTable, RenderMultiSelectTable} from '../../helperFunctions.jsx'

import ProductsPage from '../ProductsPage/ProductsPage.jsx';
import FixedCostsPage from '../FixedCostsPage/FixedCostsPage.jsx';
import OtherVariablesCost from '../Misc/OtherVariblesCost.jsx';
import CostAllocations from '../Misc/CostAllocations.jsx';


function DashboardSideBar({onClickUpdateCalc}) {
    const sidebarRef = useRef();

    document.addEventListener('scroll', () => {
        if (sidebarRef.current) {
            sidebarRef.current.style.top = `${window.scrollY + 50}px`;
        }
    });

    return (
        <div className="fb-dashboard-sidebar" ref={sidebarRef}>
            <SidebarButton name={"Products"} icon={<ProductFilled className='sidebar-icon' />} itemInModal={<ProductsPage showNavigationControls={false} />} />
            <SidebarButton name={"Cost Allocations"} icon={<DollarCircleFilled className='sidebar-icon' />} itemInModal={<CostAllocations />} />
            <SidebarButton name={"Fixed Costs"} icon={<BookFilled className='sidebar-icon' />} itemInModal={<FixedCostsPage showNavigationControls={false} />} />
            <SidebarButton name={"Other Variables Cost"} icon={<SettingFilled className='sidebar-icon' />} itemInModal={<OtherVariablesCost />} />
            <SidebarButton onClickUpdateCalc={onClickUpdateCalc} isModal={false} name={"Calculate"} icon={<CalculatorFilled className='sidebar-icon' />} />
        </div>
    );
}

function Dashboard() {
    const { setPageNum, companySelected, messageApi } = useContext(FinancialBuilderContext);
    const [formattedData, setFormattedData] = useState([["A","A"],["A","A"]]);
    const [percentageData, setPercentageData] = useState([["A","A"],["A","A"]]);
    const [dataLoading, setDataLoading] = useState(true);
    const [firm_wide_data, setFirm_wide_data] = useState({});

    const getUpdatedCalculations = useCallback(async() => {
        messageApi.loading('Fetching updated calculations...', 0);
        try {
            const response = await genericPOSTDatabaseCall('/api/fb/getCalculationData', {company: companySelected}, "calculation data");
            console.log('Updated calculation data:', response);
            setFormattedData(response.finalDATA.formattedData);
            setPercentageData(response.finalDATA.percentage_data);
            setFirm_wide_data({
                firmWideStat: response.finalDATA.firm_wide_stat,
                firmWideStatPercentage: response.finalDATA.firm_wide_stat_percentage,
                otherFirmWideStat: response.finalDATA.other_firm_wide_stat,
                arrRatiosFirmWide: response.finalDATA.arr_ratios_firm_wide
            });
            messageApi.destroy();
            messageApi.success('Calculations updated successfully');
        } catch (error) {
            console.error('Error fetching calculation data:', error);
            messageApi.destroy();
            messageApi.error('Failed to fetch updated calculations');
        }
        setDataLoading(false); // Only set to false after first load
    }, [companySelected, messageApi])

    useEffect(() => {
        getUpdatedCalculations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="fb-dashboard-overview financial-builder-container entity-industry-container">
            <h2>My CFO - Your Financial Partner</h2>
            <ShowAndHide title={"Company Info"}>
                <CompanyDetails company={companySelected} editable={false} />
            </ShowAndHide>
            {!dataLoading && 
            <>
                <ShowAndHide defaultOpen title="Financial Information Analysis Firm Wide" className="fb-dashboard-overview-content">
                    <div className='flex-row flex-space-around'>
                        <RenderOneTable name={"Firm Wide Stat %"} data={firm_wide_data.firmWideStatPercentage} />
                        <RenderOneTable name={"Employees & Price Firm Wide Stat %"} data={firm_wide_data.firmWideStat} />
                        <RenderOneTable name={"Other Firm Wide Stat"} data={firm_wide_data.otherFirmWideStat} />
                        <RenderOneTable name={"ARR Ratios Firm Wide"} data={firm_wide_data.arrRatiosFirmWide} />
                    </div>
                </ShowAndHide>
                <ShowAndHide defaultOpen title="Projections & Growth" className="fb-dashboard-overview-content">
                    <RenderMultiSelectTable data={formattedData} />
                    <RenderMultiSelectTable data={percentageData} />
                </ShowAndHide>
                <ShowAndHide defaultOpen title="Financial Projections" className="fb-dashboard-overview-content">
                <FinancialProjectionChart finalDATA={{ formattedData, percentageData }} />
                </ShowAndHide>
                <ShowAndHide defaultOpen title="Revenue Growth By Product %" className="fb-dashboard-overview-content">
                <RevenueGrowthChart finalDATA={{ formattedData, percentageData }} />
                </ShowAndHide>
                <ShowAndHide defaultOpen title="Fixed vs Variable Costs" className="fb-dashboard-overview-content">
                    <CostOverviewChart finalDATA={{ formattedData, percentageData }} />
                </ShowAndHide>
                <ShowAndHide defaultOpen title="Revenues & Cost of Revenue %" className="fb-dashboard-overview-content">
                    <RevenueVsCostChart finalDATA={{ formattedData, percentageData }} />
                </ShowAndHide>
                <ShowAndHide defaultOpen title="Burn Rate (Monthly) & Burn rate % (Usage)" className="fb-dashboard-overview-content">
                    <BurnRateChart finalDATA={{ formattedData, percentageData }} />
                </ShowAndHide>
            </>
            }
            <div className="navigation-button-container">
                <button type="button" onClick={() => setPageNum(2)} className="next-button">Back</button>
            </div>
            <DashboardSideBar onClickUpdateCalc={getUpdatedCalculations} />
        </div>
    )
}

export default Dashboard;